import React, { useState, useEffect, useContext, ReactElement } from "react";

// react-router components
import { Routes, Route, Navigate, useLocation } from "react-router-dom";

// @mui material components
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import Icon from "@mui/material/Icon";

// Argon Dashboard 2 MUI components
import Box from "themeComponents/Box";

// Argon Dashboard 2 MUI example components
import Sidenav from "themeComponents/Sidenav";
import Configurator from "themeComponents/Configurator";

// Argon Dashboard 2 MUI themes
import theme from "assets/theme";
import themeRTL from "assets/theme/theme-rtl";
import themeDark from "assets/theme-dark";
import themeDarkRTL from "assets/theme-dark/theme-rtl";

// RTL plugins
import rtlPlugin from "stylis-plugin-rtl";
import { CacheProvider, EmotionCache } from "@emotion/react";
import createCache from "@emotion/cache";

// Argon Dashboard 2 MUI routes
import routes from "routes";

// Argon Dashboard 2 MUI contexts
import { useArgonController, setMiniSidenav, setOpenConfigurator } from "context";

// Images
import brand from "assets/images/logo-ct.png";
import brandDark from "assets/images/logo-ct-dark.png";

// Icon Fonts
// import "assets/css/nucleo-icons.css";
// import "assets/css/nucleo-svg.css";
import { appLogo, appName } from "configs/constants";
import { Alert, AlertTitle } from "@mui/material";
import { AppContext } from "context/appContext";
import api, { COMPCHECK_URL, subdomainApi } from "configs/api";
import NotFoundPage from "layouts/notFound";
import { useSelector } from "react-redux";
import { login } from "slices/authSlice";
import { useDispatch } from "react-redux";
import { RootState } from "configs/store";
import useAuth from "hooks/useAuth";

interface RouteType {
  route?: string;
  component?: ReactElement;
  key?: string;
  auth?: boolean | null;
  collapse?: RouteType[];
}

export default function App(): React.JSX.Element {
  const { user } = useSelector((state: RootState) => state.auth);
  const { isCompany, isCompanyEmployee } = useAuth();
  const dispatch = useDispatch();
  const [controller, dispatchController] = useArgonController();
  const [isInvalidDomain, setIsInvalidDomain] = useState<boolean>(true);
  const { miniSidenav, direction, layout, openConfigurator, sidenavColor, darkSidenav, darkMode } =
    controller;
  const [onMouseEnter, setOnMouseEnter] = useState<boolean>(false);
  const [rtlCache, setRtlCache] = useState<EmotionCache | null>(null);
  const { pathname } = useLocation();
  const { alertData } = useContext(AppContext);
  const domainName = window.location.hostname;

  const verifySubdomain = async (subdomain: string): Promise<void> => {
    try {
      const response = await subdomainApi.get(`verifySubdomain`, { params: { subdomain } });
      setIsInvalidDomain(response.data.status);
      if (response.data.status) {
        localStorage.setItem("logo", response.data.result.logo);
      }
    } catch (error) {
      // Handle error silently
    }
  };

  // RTL cache + optional subdomain verification (must run in useEffect, not useMemo)
  useEffect(() => {
    const cacheRtl = createCache({
      key: "rtl",
      stylisPlugins: [rtlPlugin],
    });

    setRtlCache(cacheRtl);
    if (domainName !== (process.env.REACT_APP_CLIENT_DOMAIN || "")) {
      void verifySubdomain(domainName.split(".")[0]);
    }
  }, [domainName]);

  // Open sidenav when mouse enter on mini sidenav
  const handleOnMouseEnter = (): void => {
    if (miniSidenav && !onMouseEnter) {
      setMiniSidenav(dispatchController, false);
      setOnMouseEnter(true);
    }
  };

  // Close sidenav when mouse leave mini sidenav
  const handleOnMouseLeave = (): void => {
    if (onMouseEnter) {
      setMiniSidenav(dispatchController, true);
      setOnMouseEnter(false);
    }
  };

  // Change the openConfigurator state
  const handleConfiguratorOpen = (): void => setOpenConfigurator(dispatchController, !openConfigurator);

  // Setting the dir attribute for the body element
  useEffect(() => {
    document.body.setAttribute("dir", direction);
  }, [direction]);

  // Setting page scroll to 0 when changing the route
  useEffect(() => {
    document.documentElement.scrollTop = 0;
    if (document.scrollingElement) {
      document.scrollingElement.scrollTop = 0;
    }
  }, [pathname]);

  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const token = urlParams.get("token");
    const userParam = urlParams.get("user");
    const logoURL = urlParams.get("logoURL");
    const subdomain = urlParams.get("subdomain");

    if (token && userParam) {
      try {
        // Save to localStorage
        localStorage.setItem("token", token);
        localStorage.setItem("user", userParam);
        if (logoURL) {
          localStorage.setItem("logo", logoURL);
        }

        dispatch(
          login({
            token: token,
            user: JSON.parse(userParam),
            subdomain: subdomain ? JSON.parse(subdomain) : undefined,
          })
        );
        // Remove query params and refresh
        const cleanUrl = window.location.origin + window.location.pathname;
        window.history.replaceState({}, document.title, cleanUrl);
      } catch (error) {
        console.error("Error saving login data:", error);
      }
    }
  }, [dispatch]);

  const getRoutes = (allRoutes: RouteType[]): React.JSX.Element[] =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getRoutes(route.collapse);
      }

      if (route.route && route.component) {
        if (route.auth === false || route.auth === null) {
          return <Route key={route.key} path={route.route} element={route.component} />;
        }
      }

      return [];
    });

  const getAuthRoutes = (allRoutes: RouteType[]): React.JSX.Element[] =>
    allRoutes.flatMap((route) => {
      if (route.collapse) {
        return getAuthRoutes(route.collapse);
      }

      if (route.route && route.component) {
        if (route.auth === true) {
          return <Route key={route.key} path={route.route} element={route.component} />;
        }
      }

      return [];
    });

  const configsButton = (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      width="3.5rem"
      height="3.5rem"
      bgColor="white"
      shadow="sm"
      borderRadius="50%"
      position="fixed"
      right="2rem"
      bottom="2rem"
      zIndex={99}
      color="dark"
      sx={{ cursor: "pointer" }}
      onClick={handleConfiguratorOpen}
    >
      <Icon fontSize="medium" color="inherit">
        settings
      </Icon>
    </Box>
  );

  return isInvalidDomain ? (
    direction === "rtl" ? (
      <CacheProvider value={rtlCache!}>
        <ThemeProvider theme={darkMode ? themeDarkRTL : themeRTL}>
          <CssBaseline />
          {layout === "dashboard" && (
            <>
              <Sidenav
                color={sidenavColor}
                brand={
                  localStorage.getItem("logo")
                    ? `${COMPCHECK_URL}/api/${localStorage.getItem("logo")}`
                    : appLogo
                }
                brandName={
                    isCompany
                    ? user?.companyName
                      : isCompanyEmployee
                    ? user?.companyID?.companyName
                    : appName
                }
                routes={routes}
                onMouseEnter={handleOnMouseEnter}
                onMouseLeave={handleOnMouseLeave}
              />
              {/* <Configurator /> */}
              {/* {configsButton} */}
            </>
          )}
          {layout === "vr" && <Configurator />}
          {alertData.message && alertData.type && (
            <Box sx={{ position: "fixed", top: "10px", right: "30px", zIndex: "999" }}>
              <Alert severity={alertData.type}>
                {alertData.title && <AlertTitle>{alertData.title}</AlertTitle>}
                {alertData.message}
              </Alert>
            </Box>
          )}
          <Routes>
            {localStorage.getItem("token") ? getRoutes(routes) : getAuthRoutes(routes)}
            {localStorage.getItem("token") ? (
              <Route path="*" element={<Navigate to="/dashboard" />} />
            ) : (
              <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
            )}
          </Routes>
        </ThemeProvider>
      </CacheProvider>
    ) : (
      <ThemeProvider theme={darkMode ? themeDark : theme}>
        <CssBaseline />
        {layout === "dashboard" && (
          <>
            <Sidenav
              color={sidenavColor}
              brand={
                localStorage.getItem("logo")
                  ? `${COMPCHECK_URL}/api/${localStorage.getItem("logo")}`
                  : appLogo
              }
              brandName={
                  isCompany
                  ? user?.name
                    : isCompanyEmployee
                  ? user?.companyID?.name
                  : appName
              }
              routes={routes}
              onMouseEnter={handleOnMouseEnter}
              onMouseLeave={handleOnMouseLeave}
            />
            {/* <Configurator /> */}
            {/* {configsButton} */}
          </>
        )}
        {alertData.message && alertData.type && (
          <Box sx={{ position: "fixed", top: "10px", right: "30px", zIndex: "999" }}>
            <Alert severity={alertData.type}>
              {alertData.title && <AlertTitle>{alertData.title}</AlertTitle>}
              {alertData.message}
            </Alert>
          </Box>
        )}
        {layout === "vr" && <Configurator />}
        <Routes>
          {localStorage.getItem("token") ? getRoutes(routes) : getAuthRoutes(routes)}
          {localStorage.getItem("token") ? (
            <Route path="*" element={<Navigate to="/dashboard" />} />
          ) : (
            <Route path="*" element={<Navigate to="/authentication/sign-in" />} />
          )}
        </Routes>
      </ThemeProvider>
    )
  ) : (
    <NotFoundPage />
  );
}

